import { useEffect, useState } from "react";
import { Modal } from 'react-bootstrap';
import { NumericTextBox } from '@progress/kendo-react-inputs';
import { PrivateAxios } from "../../environment/AxiosInstance";
import { toast } from "react-toastify";
import { CurrencyList } from '../../utils/StaticData';
import Select from 'react-select';
import moment from "moment";
import { UserAuth } from '../auth/Auth';

const modalStyles = `
  .dropdown-popup-high-z { z-index: 10000 !important; }
  .k-animation-container { z-index: 10000 !important; }
  .k-popup { z-index: 10000 !important; }
  .k-list-container { z-index: 10000 !important; }
`;

export default function EditTargetModal({ show, onHide, onClose, target, onUpdated, onSuccess }) {
  const { User, MatchPermission } = UserAuth();

  const [formData, setFormData] = useState({
    title: '',
    assignees: [],
    totalAmount: 0,
    period: 'daily',
    currency: 'INR',
    targetType: 'amount',
    totalNumber: 0,
    startDate: '',
    endDate: ''
  });
  const [useIndividualTargets, setUseIndividualTargets] = useState(false);
  const [individualTargets, setIndividualTargets] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.innerHTML = modalStyles;
    document.head.appendChild(styleElement);
    return () => {
      if (document.head.contains(styleElement)) {
        document.head.removeChild(styleElement);
      }
    };
  }, []);

  useEffect(() => {
    if (show && target) {
      setFormData({
        title: target.title || '',
        assignees: target.assignees || [],
        totalAmount: target.target_amount || 0,
        period: target.period || 'daily',
        currency: target.currency || 'INR',
        targetType: target.target_type || 'amount',
        totalNumber: target.target_number || 0,
        startDate: target.start_date ? moment(target.start_date).format('YYYY-MM-DD') : '',
        endDate: target.end_date ? moment(target.end_date).format('YYYY-MM-DD') : ''
      });

      const map = {};
      (target.assignees || []).forEach((a) => {
        const uid = a.assignee_id || (a.assignee && a.assignee.id);
        if (uid != null) {
          map[uid] = {
            amount: a.assigned_amount != null ? String(a.assigned_amount) : '',
            number: a.assigned_number != null ? String(a.assigned_number) : ''
          };
        }
      });
      setIndividualTargets(map);

      // Auto-enable individual mode when existing per-user values diverge
      const list = target.assignees || [];
      if (list.length > 1) {
        const targetTypeNow = target.target_type || 'amount';
        const vals = list.map(a => Number(targetTypeNow === 'amount' ? a.assigned_amount : a.assigned_number) || 0);
        const allEqual = vals.every(v => v === vals[0]);
        setUseIndividualTargets(!allEqual);
      } else {
        setUseIndividualTargets(false);
      }
    }
  }, [show, target]);

  if (!MatchPermission(['Sales All', 'Edit Sales', 'Doer Edit Sales'])) {
    return null;
  }

  const handleModalClose = () => {
    if (onHide) onHide();
    else if (onClose) onClose();
  };

  const handleSuccess = () => {
    if (onUpdated) onUpdated();
    else if (onSuccess) onSuccess();
  };

  const handleAssigneeChange = (selectedOptions) => {
    const list = selectedOptions || [];
    setFormData(prev => ({ ...prev, assignees: list }));
    setIndividualTargets((prev) => {
      const next = {};
      list.forEach((opt) => {
        const uid = opt.value || opt.id || opt.assignee_id;
        if (uid != null) {
          next[uid] = prev[uid] || {
            amount: formData.targetType === 'amount' ? String(formData.totalAmount || '') : '',
            number: formData.targetType === 'number' ? String(formData.totalNumber || '') : ''
          };
        }
      });
      return next;
    });
  };

  const individualTotal = () => {
    return formData.assignees.reduce((sum, a) => {
      const uid = a.assignee_id || (a.assignee && a.assignee.id) || a.id || a.value;
      const v = individualTargets[uid] || {};
      const raw = formData.targetType === 'amount' ? v.amount : v.number;
      const n = Number(raw);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title) {
      toast.warning("Please fill all required fields");
      return;
    }
    if (formData.assignees.length === 0) {
      toast.warning("Please select at least one assignee");
      return;
    }
    if (!formData.startDate || !formData.endDate) {
      toast.warning("Start date and end date are required");
      return;
    }
    if (moment(formData.endDate).isBefore(moment(formData.startDate), 'day')) {
      toast.warning("End date must be on or after start date");
      return;
    }

    if (useIndividualTargets) {
      for (const a of formData.assignees) {
        const uid = a.assignee_id || (a.assignee && a.assignee.id) || a.id || a.value;
        const v = individualTargets[uid] || {};
        const raw = formData.targetType === 'amount' ? v.amount : v.number;
        const n = Number(raw);
        if (raw === '' || raw === undefined || raw === null || !Number.isFinite(n) || n <= 0) {
          toast.warning("Enter a valid target for " + (a.name || a.label || 'each assignee'));
          return;
        }
      }
    } else {
      if (formData.targetType === 'amount' && !formData.totalAmount) {
        toast.warning("Please enter target amount");
        return;
      }
      if (formData.targetType === 'number' && !formData.totalNumber) {
        toast.warning("Please enter target number");
        return;
      }
    }

    if (formData.targetType === 'amount' && target && formData.currency !== (target.currency || 'INR')) {
      const hasExistingAchievements = (target.assignees || []).some(a => {
        return (Number(a.rnc_ach) || 0) + (Number(a.rec_ach) || 0) > 0;
      });
      if (hasExistingAchievements) {
        const ok = window.confirm(
          "You are changing the currency from " + (target.currency || 'INR') + " to " + formData.currency + ". " +
          "Existing achievement values will NOT be auto-converted. Continue?"
        );
        if (!ok) return;
      }
    }

    setIsSubmitting(true);
    try {
      const originalAssigneeIds = (target.assignees || []).map(a => a.assignee_id) || [];

      const currentAssigneeIds = formData.assignees.map(assigneeObj => {
        return assigneeObj.assignee_id || (assigneeObj.assignee && assigneeObj.assignee.id) || assigneeObj.id || assigneeObj.value;
      });

      const totalAmount = useIndividualTargets && formData.targetType === 'amount'
        ? individualTotal()
        : Number(formData.totalAmount) || 0;
      const totalNumber = useIndividualTargets && formData.targetType === 'number'
        ? individualTotal()
        : Number(formData.totalNumber) || 0;

      const payload = {
        title: formData.title,
        targetAmount: totalAmount,
        targetNumber: totalNumber,
        targetType: formData.targetType,
        period: formData.period,
        currency: formData.currency,
        startDate: formData.startDate,
        endDate: formData.endDate,
        useIndividualTargets: !!useIndividualTargets
      };

      if (useIndividualTargets) {
        payload.assigneeUpdates = formData.assignees.map((assignee) => {
          const uid = assignee.assignee_id || (assignee.assignee && assignee.assignee.id) || assignee.id || assignee.value;
          const v = individualTargets[uid] || {};
          return {
            assignee_id: uid,
            assigned_amount: formData.targetType === 'amount' ? Number(v.amount) || 0 : 0,
            assigned_number: formData.targetType === 'number' ? Number(v.number) || 0 : 0
          };
        });
        const newIds = currentAssigneeIds.filter(id => !originalAssigneeIds.includes(id));
        if (newIds.length > 0) payload.assignees = newIds;
      } else {
        payload.assigneeUpdates = formData.assignees.map((assignee) => {
          const uid = assignee.assignee_id || (assignee.assignee && assignee.assignee.id) || assignee.id || assignee.value;
          return {
            assignee_id: uid,
            assigned_amount: formData.targetType === 'amount' ? totalAmount : 0,
            assigned_number: formData.targetType === 'number' ? totalNumber : 0
          };
        });
        const newIds = currentAssigneeIds.filter(id => !originalAssigneeIds.includes(id));
        if (newIds.length > 0) payload.assignees = newIds;
      }

      await PrivateAxios.put("/sales-targets/" + target.id, payload);
      toast.success("Target updated successfully");

      handleSuccess();
      handleModalClose();
    } catch (err) {
      console.error('Update target error:', err);
      toast.error((err.response && err.response.data && err.response.data.message) || "Failed to update target");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      assignees: [],
      totalAmount: 0,
      period: 'daily',
      currency: 'INR',
      targetType: 'amount',
      totalNumber: 0,
      startDate: '',
      endDate: ''
    });
    setIndividualTargets({});
    setUseIndividualTargets(false);
    handleModalClose();
  };

  const selectedAssignees = formData.assignees
    .map(assigneeObj => {
      const userId = assigneeObj.assignee_id || (assigneeObj.assignee && assigneeObj.assignee.id) || assigneeObj.id || assigneeObj.value;
      const matchedUser = User && User.find(user => user.id === userId);
      if (matchedUser) {
        return { value: matchedUser.id, label: matchedUser.name, ...matchedUser };
      }
      return null;
    })
    .filter(Boolean);

  const currencySymbol = (CurrencyList.find(c => c.code === formData.currency) || {}).symbol || 'Rs';

  return (
    <Modal show={show} onHide={handleClose} backdrop="static" centered size="lg">
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
          <i className="fas fa-edit me-2"></i>
          Edit Target
        </Modal.Title>
      </Modal.Header>

      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="row">
            <div className="col-12 mb-3">
              <label className="form-label">
                Target Title <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter target title"
                required
              />
            </div>

            <div className="col-12 mb-3">
              <label className="form-label">
                Assignees <span className="text-danger">*</span>
              </label>
              <Select
                isMulti
                options={User}
                value={selectedAssignees}
                onChange={handleAssigneeChange}
                placeholder="Select assignees..."
                className="react-select-container"
                classNamePrefix="react-select"
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                noOptionsMessage={() => "No users available"}
              />
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label">
                Start Date <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className="form-control"
                value={formData.startDate}
                onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                required
              />
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label">
                End Date <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className="form-control"
                value={formData.endDate}
                onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                min={formData.startDate || undefined}
                required
              />
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label">
                Target Type <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                value={formData.targetType}
                onChange={(e) => setFormData(prev => ({ ...prev, targetType: e.target.value }))}
                disabled
                required
              >
                <option value="amount">Amount Target</option>
                <option value="number">Number Target</option>
              </select>
              <small className="text-muted">Target type cannot be modified</small>
            </div>

            {formData.targetType === "amount" && (
              <div className="col-md-6 mb-3">
                <label className="form-label">Currency</label>
                <select
                  className="form-select"
                  value={formData.currency}
                  onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                >
                  {CurrencyList.map(currency => (
                    <option key={currency.code} value={currency.code}>
                      {currency.symbol} - {currency.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="col-12 mb-3">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="editUseIndividualTargetsSwitch"
                  checked={useIndividualTargets}
                  onChange={(e) => setUseIndividualTargets(e.target.checked)}
                  disabled={formData.assignees.length === 0}
                />
                <label className="form-check-label" htmlFor="editUseIndividualTargetsSwitch">
                  <i className="fas fa-users-cog me-2"></i>
                  Update individual target per assignee
                </label>
              </div>
              <small className="text-muted">
                When enabled, set a different value for each assignee. The parent total is the sum across users.
              </small>
            </div>

            {!useIndividualTargets && formData.targetType === "amount" && (
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Total Amount <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <span className="input-group-text">{currencySymbol}</span>
                  <NumericTextBox
                    value={formData.totalAmount}
                    onChange={(e) => setFormData(prev => ({ ...prev, totalAmount: e.value }))}
                    format="n0"
                    min={0}
                    className="form-control"
                    placeholder="Enter target amount"
                  />
                </div>
              </div>
            )}

            {!useIndividualTargets && formData.targetType === "number" && (
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Total Number <span className="text-danger">*</span>
                </label>
                <NumericTextBox
                  value={formData.totalNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, totalNumber: e.value }))}
                  format="n0"
                  min={0}
                  className="form-control"
                  placeholder="Enter target number"
                />
              </div>
            )}

            <div className="col-12 mb-3">
              <label className="form-label">
                Period <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                value={formData.period}
                onChange={(e) => setFormData(prev => ({ ...prev, period: e.target.value }))}
                disabled
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </select>
              <small className="text-muted">Target period cannot be modified</small>
            </div>

            {useIndividualTargets && formData.assignees.length > 0 && (
              <div className="col-12 mb-3">
                <label className="form-label">
                  Per-Assignee {formData.targetType === 'amount' ? 'Amount' : 'Number'} <span className="text-danger">*</span>
                </label>
                <div className="table-responsive" style={{ border: '1px solid #e8e4ff', borderRadius: 8, overflow: 'hidden' }}>
                  <table className="table table-bordered mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '45%' }}>Assignee</th>
                        <th>
                          {formData.targetType === 'amount'
                            ? ('Target Amount (' + currencySymbol + ')')
                            : 'Target Number'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.assignees.map((a) => {
                        const uid = a.assignee_id || (a.assignee && a.assignee.id) || a.id || a.value;
                        const display = a.name || a.label || (a.assignee && a.assignee.name) || 'Unknown';
                        const email = a.email || (a.assignee && a.assignee.email);
                        const v = individualTargets[uid] || {};
                        const fieldVal = formData.targetType === 'amount'
                          ? (v.amount || '')
                          : (v.number || '');
                        const setField = (key, val) => {
                          setIndividualTargets((prev) => ({
                            ...prev,
                            [uid]: { ...(prev[uid] || {}), [key]: val }
                          }));
                        };
                        return (
                          <tr key={uid}>
                            <td>
                              <strong>{display}</strong>
                              {email && <div className="small text-muted">{email}</div>}
                            </td>
                            <td>
                              <div className="input-group">
                                {formData.targetType === 'amount' && (
                                  <span className="input-group-text">{currencySymbol}</span>
                                )}
                                <input
                                  type="number"
                                  className="form-control"
                                  placeholder={"Enter " + (formData.targetType === 'amount' ? 'amount' : 'number')}
                                  value={fieldVal}
                                  min="0"
                                  step={formData.targetType === 'amount' ? '0.01' : '1'}
                                  onChange={(e) => setField(formData.targetType === 'amount' ? 'amount' : 'number', e.target.value)}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="table-light">
                        <td className="text-end"><strong>Total</strong></td>
                        <td>
                          <strong>
                            {formData.targetType === 'amount'
                              ? (currencySymbol + new Intl.NumberFormat('en-IN').format(individualTotal()))
                              : new Intl.NumberFormat('en-IN').format(individualTotal())}
                          </strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            <i className="fas fa-times me-2"></i>Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Updating...
              </>
            ) : (
              <>
                <i className="fas fa-save me-2"></i>Update Target
              </>
            )}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
